import { API_BASE_URL } from '../types';

export interface BankResponse {
  id: string;
  name: string;
  description: string;
  logo?: string;
  fileTypes: string[];
}

export interface BanksApiResponse {
  success: boolean;
  country?: string;
  locale: string;
  banks: BankResponse[] | Record<string, BankResponse[]>;
}

export interface BankConfigResponse {
  success: boolean;
  bank: {
    id: string;
    name: string;
    description: string;
    logo?: string;
    config: any;
    converterFunction: string;
    fileTypes: string[];
    fileConfig?: any;
  };
}

/**
 * Fetch banks for a specific country
 */
export async function fetchBanksByCountry(countryId: string, locale: string = 'en-us'): Promise<BankResponse[]> {
  const response = await fetch(`${API_BASE_URL}/banks?country=${countryId}&locale=${locale}`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch banks for ${countryId}: ${response.statusText}`);
  }
  
  const data: BanksApiResponse = await response.json();
  
  if (!data.success) {
    throw new Error('Failed to fetch banks');
  }
  
  return data.banks as BankResponse[];
}

/**
 * Fetch all banks grouped by country
 */
export async function fetchAllBanks(locale: string = 'en-us'): Promise<Record<string, BankResponse[]>> {
  const response = await fetch(`${API_BASE_URL}/banks?locale=${locale}`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch banks: ${response.statusText}`);
  }
  
  const data: BanksApiResponse = await response.json();
  
  if (!data.success) {
    throw new Error('Failed to fetch banks');
  }
  
  return data.banks as Record<string, BankResponse[]>;
}

/**
 * Fetch bank configuration by ID
 */
export async function fetchBankConfig(bankId: string): Promise<BankConfigResponse['bank']> {
  const response = await fetch(`${API_BASE_URL}/banks/config?bankId=${bankId}`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch bank config for ${bankId}: ${response.statusText}`);
  }
  
  const data: BankConfigResponse = await response.json();
  
  if (!data.success) {
    throw new Error('Failed to fetch bank config');
  }
  
  return data.bank;
}

/**
 * Flatten all banks from grouped data
 */
export function flattenBanks(groupedBanks: Record<string, BankResponse[]>): BankResponse[] {
  return Object.values(groupedBanks).flat();
} 